import { useMemo } from 'react'
import axios from 'axios'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { extractErrorMessage } from '../utils/apitools'
import useAccount from './useAccount'
import useSnackbar from './useSnackbar'
import useLoading from './useLoading'
import { API_BASE_URL } from '../constants/system'
import { AutoCoderVariable, AutoCoderCode } from '../types/gotypes'

// Framework data structure stored in the auto_coder_frameworks table
export interface FrameworkData {
  name: string
  main_variable: AutoCoderVariable
  suggestions?: AutoCoderCode[]
  looped_variables?: AutoCoderVariable[]
}

// Framework library entry
export interface AutoCoderFrameworkLibrary {
  id: string
  created_at: number
  updated_at: number
  customer: string
  job: string
  name: string
  description?: string
  data?: FrameworkData
  source_pipeline_id?: string
  created_by: string
  labels?: Record<string, string>
  /** Library tier: 'company' (Company-level, set by a Manager) or 'user'. */
  visibility?: 'user' | 'company'
  /** Dynamic frames (docs/autocoder-dynamic-frames.md): collects the model's suggestions for new codes. */
  dynamic?: boolean
  /** The frame's version: 1 until a suggestion is accepted. */
  version?: number
  /** Pending suggestions, on dynamic frames in the list. */
  pending_suggestions?: number
}

interface UseFrameworkLibraryOptions {
  customer: string
  job: string
  labels?: Record<string, string>
  frameworkId?: string
}

interface CreateFrameworkRequest {
  name: string
  description?: string
  data: FrameworkData
  labels?: Record<string, string>
}

interface UpdateFrameworkRequest {
  name?: string
  description?: string
  data?: FrameworkData
  labels?: Record<string, string>
}

interface ApplyFrameworkResponse {
  message: string
  pipeline: unknown
}

interface ExtractFrameworkRequest {
  name?: string
  description?: string
  labels?: Record<string, string>
}

export const useFrameworkLibrary = (options: UseFrameworkLibraryOptions) => {
  const { customer, job, labels, frameworkId } = options

  const account = useAccount()
  const snackbar = useSnackbar()
  const loading = useLoading()
  const queryClient = useQueryClient()

  // Build labels query param string: "key1:value1,key2:value2"
  const labelsParam = useMemo(() => {
    if (!labels || Object.keys(labels).length === 0) return undefined
    return Object.entries(labels).map(([k, v]) => `${k}:${v}`).join(',')
  }, [labels])

  // Query keys
  const listQueryKey = ['frameworks', { customer, job, labels: labelsParam }]
  const frameworkQueryKey = ['framework', { customer, job, frameworkId }]

  // ============ Framework List Operations ============

  // List frameworks with optional label filter
  const frameworksQuery = useQuery({
    queryKey: listQueryKey,
    enabled: Boolean(customer && job && account.user),
    queryFn: async () => {
      if (!customer || !job || !account.user) return []
      try {
        const res = await axios.get<AutoCoderFrameworkLibrary[]>(
          `${API_BASE_URL}/autocoder/${customer}/${job}/frameworks`,
          { params: { labels: labelsParam } }
        )
        return res.data
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  // Get single framework by ID
  const frameworkQuery = useQuery({
    queryKey: frameworkQueryKey,
    enabled: Boolean(customer && job && frameworkId && frameworkId !== 'new-manual' && frameworkId !== 'new-themeai' && account.user),
    queryFn: async () => {
      if (!customer || !job || !frameworkId || frameworkId === 'new-manual' || frameworkId === 'new-themeai' || !account.user) return null
      try {
        const res = await axios.get<AutoCoderFrameworkLibrary>(
          `${API_BASE_URL}/autocoder/${customer}/${job}/frameworks/${frameworkId}`
        )
        return res.data
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  // ============ Framework CRUD Mutations ============

  // Create framework
  const createFrameworkMutation = useMutation({
    mutationFn: async (payload: CreateFrameworkRequest) => {
      loading.setLoading(true)
      const res = await axios.post<AutoCoderFrameworkLibrary>(
        `${API_BASE_URL}/autocoder/${customer}/${job}/frameworks`,
        payload
      )
      return res.data
    },
    onSuccess: () => {
      loading.setLoading(false)
      queryClient.invalidateQueries({ queryKey: listQueryKey })
      snackbar.success('Framework created successfully')
    },
    onError: (error) => {
      loading.setLoading(false)
      snackbar.error(extractErrorMessage(error))
    },
  })

  // Update framework
  const updateFrameworkMutation = useMutation({
    mutationFn: async (payload: { id: string } & UpdateFrameworkRequest) => {
      loading.setLoading(true)
      const { id, ...data } = payload
      const res = await axios.put<AutoCoderFrameworkLibrary>(
        `${API_BASE_URL}/autocoder/${customer}/${job}/frameworks/${id}`,
        data
      )
      return res.data
    },
    onSuccess: () => {
      loading.setLoading(false)
      queryClient.invalidateQueries({ queryKey: listQueryKey })
      queryClient.invalidateQueries({ queryKey: frameworkQueryKey })
      snackbar.success('Framework updated successfully')
    },
    onError: (error) => {
      loading.setLoading(false)
      snackbar.error(extractErrorMessage(error))
    },
  })

  // Delete framework
  const deleteFrameworkMutation = useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${API_BASE_URL}/autocoder/${customer}/${job}/frameworks/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
      snackbar.success('Framework deleted successfully')
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  // Apply framework to pipeline
  const applyFrameworkMutation = useMutation({
    mutationFn: async (payload: { pipelineId: string; frameworkId: string }) => {
      loading.setLoading(true)
      const res = await axios.post<ApplyFrameworkResponse>(
        `${API_BASE_URL}/autocoder/${customer}/${job}/pipelines/${payload.pipelineId}/apply-framework/${payload.frameworkId}`
      )
      return res.data
    },
    onSuccess: () => {
      loading.setLoading(false)
      queryClient.invalidateQueries({ queryKey: ['autocoder_pipelines'] })
      queryClient.invalidateQueries({ queryKey: ['autocoder_pipeline'] })
      snackbar.success('Framework applied to pipeline')
    },
    onError: (error) => {
      loading.setLoading(false)
      snackbar.error(extractErrorMessage(error))
    },
  })

  // Extract framework from pipeline
  const extractFrameworkMutation = useMutation({
    mutationFn: async (payload: { pipelineId: string } & ExtractFrameworkRequest) => {
      loading.setLoading(true)
      const { pipelineId, ...data } = payload
      const res = await axios.post<AutoCoderFrameworkLibrary>(
        `${API_BASE_URL}/autocoder/${customer}/${job}/pipelines/${pipelineId}/extract-framework`,
        data
      )
      return res.data
    },
    onSuccess: () => {
      loading.setLoading(false)
      queryClient.invalidateQueries({ queryKey: listQueryKey })
      snackbar.success('Framework extracted from pipeline')
    },
    onError: (error) => {
      loading.setLoading(false)
      snackbar.error(extractErrorMessage(error))
    },
  })

  // ============ Convenience Methods ============

  const createFramework = async (payload: CreateFrameworkRequest) => {
    return createFrameworkMutation.mutateAsync(payload)
  }

  const updateFramework = async (id: string, payload: UpdateFrameworkRequest) => {
    return updateFrameworkMutation.mutateAsync({ id, ...payload })
  }

  const deleteFramework = (id: string) => {
    deleteFrameworkMutation.mutate(id)
  }

  const applyFramework = async (pipelineId: string, fwId: string) => {
    return applyFrameworkMutation.mutateAsync({ pipelineId, frameworkId: fwId })
  }

  const extractFramework = async (pipelineId: string, payload?: ExtractFrameworkRequest) => {
    return extractFrameworkMutation.mutateAsync({ pipelineId, ...payload })
  }

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: listQueryKey })
    queryClient.invalidateQueries({ queryKey: frameworkQueryKey })
  }

  // A Manager moves a frame to or from Company-level (#1492).
  const setVisibilityMutation = useMutation({
    mutationFn: async (payload: { id: string; visibility: 'user' | 'company' }) => {
      const res = await axios.post<AutoCoderFrameworkLibrary>(
        `${API_BASE_URL}/autocoder/${customer}/${job}/frameworks/${payload.id}/visibility`,
        { visibility: payload.visibility },
      )
      return res.data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: listQueryKey }),
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })
  const setFrameworkVisibility = (framework: AutoCoderFrameworkLibrary, visibility: 'user' | 'company') =>
    setVisibilityMutation.mutateAsync({ id: framework.id, visibility }).then(() => undefined)

  // A Manager makes a frame dynamic, or stops it collecting suggestions.
  const setDynamicMutation = useMutation({
    mutationFn: async (payload: { id: string; dynamic: boolean }) => {
      const res = await axios.put<AutoCoderFrameworkLibrary>(
        `${API_BASE_URL}/autocoder/${customer}/${job}/frameworks/${payload.id}/dynamic`,
        { dynamic: payload.dynamic },
      )
      return res.data
    },
    onSuccess: (_data, payload) => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
      queryClient.invalidateQueries({ queryKey: frameworkQueryKey })
      snackbar.success(payload.dynamic ? 'The code frame now collects suggestions' : 'The code frame no longer collects suggestions')
    },
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })
  const setFrameworkDynamic = (id: string, dynamic: boolean) =>
    setDynamicMutation.mutateAsync({ id, dynamic }).then(() => undefined)

  return {
    // Framework list
    frameworks: frameworksQuery.data ?? [],
    frameworksLoading: frameworksQuery.isLoading,
    frameworksError: frameworksQuery.error,

    // Single framework
    framework: frameworkQuery.data,
    frameworkLoading: frameworkQuery.isLoading,
    frameworkError: frameworkQuery.error,
    isNewFramework: frameworkId === 'new-manual' || frameworkId === 'new-themeai',
    isManualMode: frameworkId === 'new-manual',
    isThemeAIMode: frameworkId === 'new-themeai',

    // CRUD operations
    createFramework,
    updateFramework,
    deleteFramework,
    applyFramework,
    extractFramework,
    setFrameworkVisibility,
    setFrameworkDynamic,
    dynamicLoading: setDynamicMutation.isPending,

    // Mutation states
    createLoading: createFrameworkMutation.isPending,
    updateLoading: updateFrameworkMutation.isPending,
    deleteLoading: deleteFrameworkMutation.isPending,
    applyLoading: applyFrameworkMutation.isPending,
    extractLoading: extractFrameworkMutation.isPending,

    // Refresh
    refetch,
  }
}

export default useFrameworkLibrary
